import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { NavigationBar } from '../components/NavigationBar';
import { CheckIcon } from '@heroicons/react/24/outline';
import { useTheme } from '../context/ThemeContext';
import { subscriptionService } from '../services/subscriptionService'; // Import subscription service
import { useSelector } from 'react-redux'; // Import useSelector
import { RootState } from '../store/store'; // Import RootState

export const Pricing = () => {
  const navigate = useNavigate();
  const { darkMode } = useTheme();
  const user = useSelector((state: RootState) => state.auth.user); // Get user state
  const [contactForm, setContactForm] = useState({
    name: '',
    email: '',
    company: '',
    teamSize: '',
    message: ''
  });
  const [showContactForm, setShowContactForm] = useState(false);
  const [isLoading, setIsLoading] = useState(false); // Add loading state
  const [error, setError] = useState<string | null>(null); // Add error state

  const handleUpgradeClick = async () => {
    if (!user) {
      navigate('/login?redirect=/pricing'); // Redirect to login if not authenticated
      return;
    }
    setIsLoading(true);
    setError(null);
    try {
      const { url } = await subscriptionService.createCheckoutSession();
      window.location.href = url; // Redirect to Stripe checkout
    } catch (err) {
      console.error('Failed to create checkout session:', err);
      setError('Failed to initiate payment. Please try again.');
      setIsLoading(false);
    }
  };

  const plans = [
    {
      name: 'Free',
      price: '$0',
      period: 'forever',
      description: 'Perfect for trying out PowerQuizzer',
      features: [
        'Up to 5 collections',
        '10 flashcards per collection',
        'Basic analytics',
        'Study progress tracking',
        'Community support'
      ],
      buttonText: 'Get Started',
      buttonAction: () => navigate('/register')
    },
    {
      name: 'Premium',
      price: '$9.99',
      period: 'per month',
      description: 'For serious learners and educators',
      features: [
        'Unlimited collections',
        'Unlimited flashcards',
        'Dark mode',
        'Advanced analytics',
        'AI-powered quiz generation',
        'Priority support',
        'Export to PDF',
        'Custom branding'
      ],
      buttonText: user?.isPremium ? 'Current Plan' : 'Upgrade to Premium', // Dynamic button text
      buttonAction: user?.isPremium ? () => {} : handleUpgradeClick, // Call handleUpgradeClick
      highlight: true,
      disabled: user?.isPremium // Disable button if already premium
    },
    {
      name: 'Team',
      price: 'Custom',
      period: 'per user/month',
      description: 'For organizations and institutions',
      features: [
        'All Premium features',
        'Team management dashboard',
        'Centralized billing',
        'User roles & permissions',
        'Advanced team analytics',
        'Dedicated account manager',
        'Custom integrations',
        'Training sessions'
      ],
      buttonText: 'Contact Sales',
      buttonAction: () => setShowContactForm(true)
    }
  ];

  const handleContactSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    // Handle contact form submission
    try {
      // Add your API call here
      console.log('Contact form submitted:', contactForm);
      setShowContactForm(false);
      // Show success message
    } catch (error) {
      console.error('Error submitting form:', error);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white dark:from-gray-900 dark:to-gray-800">
      <NavigationBar />
      <div className="pt-20 pb-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <motion.h1 
              className="text-4xl font-bold tracking-tight text-gray-900 dark:text-white sm:text-5xl"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
            >
              Simple, transparent pricing
            </motion.h1>
            <motion.p 
              className="mt-5 text-xl text-gray-500 dark:text-gray-300"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
            >
              Choose the perfect plan for your learning journey
            </motion.p>
          </div>

          <motion.div 
            className="mt-16 grid gap-8 lg:grid-cols-3"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            {plans.map((plan, index) => (
              <div
                key={plan.name}
                className={`relative rounded-2xl ${
                  plan.highlight
                    ? 'bg-gradient-to-r from-indigo-500 to-purple-600 text-white shadow-xl scale-105'
                    : 'bg-white dark:bg-gray-800 text-gray-900 dark:text-white border border-gray-200 dark:border-gray-700'
                } p-8 shadow-lg`}
              >
                <h3 className={`text-2xl font-semibold ${plan.highlight ? 'text-white' : 'text-gray-900 dark:text-white'}`}>
                  {plan.name}
                </h3>
                <p className={`mt-4 text-sm ${plan.highlight ? 'text-indigo-100' : 'text-gray-500 dark:text-gray-300'}`}>
                  {plan.description}
                </p>
                <p className="mt-8">
                  <span className="text-4xl font-bold tracking-tight">{plan.price}</span>
                  <span className={`text-sm font-semibold ${plan.highlight ? 'text-indigo-100' : 'text-gray-500 dark:text-gray-300'}`}>
                    {' '}{plan.period}
                  </span>
                </p>
                <ul className="mt-10 space-y-4">
                  {plan.features.map((feature) => (
                    <li key={feature} className="flex items-start">
                      <div className={`rounded-full p-1 ${plan.highlight ? 'bg-indigo-400' : 'bg-indigo-500'}`}>
                        <svg className="h-5 w-5 text-white" viewBox="0 0 20 20" fill="currentColor">
  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
</svg>
                      </div>
                      <span className="ml-3 text-sm">{feature}</span>
                    </li>
                  ))}
                </ul>
                <button
                  onClick={plan.buttonAction}
                  disabled={isLoading || plan.disabled} // Disable button during loading or if plan disabled
                  className={`mt-8 w-full rounded-lg px-6 py-3 text-center text-sm font-semibold shadow-sm transition-all duration-200 ${
                    plan.highlight
                      ? 'bg-white text-indigo-600 hover:bg-indigo-50'
                      : 'bg-indigo-600 text-white hover:bg-indigo-500'
                  } ${(isLoading || plan.disabled) ? 'opacity-50 cursor-not-allowed' : ''}`} // Style disabled button
                >
                  {isLoading && plan.highlight ? 'Processing...' : plan.buttonText}
                </button>
              </div>
            ))}
          </motion.div>

          {error && (
            <motion.div 
              className="mt-8 text-center text-red-600 dark:text-red-400"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
            >
              {error}
            </motion.div>
          )}
        </div>
      </div>

      {/* Team Contact Form Modal */}
      {showContactForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm flex items-center justify-center p-4 z-50">
          <motion.div 
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white dark:bg-gray-800 rounded-2xl p-8 max-w-md w-full shadow-xl"
          >
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">Contact Sales Team</h2>
            <form onSubmit={handleContactSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Name
                </label>
                <input
                  type="text"
                  required
                  value={contactForm.name}
                  onChange={(e) => setContactForm(prev => ({ ...prev, name: e.target.value }))}
                  className="w-full rounded-lg border border-gray-300 dark:border-gray-600 px-4 py-2 focus:border-indigo-500 focus:ring-indigo-500 dark:bg-gray-700 dark:text-white"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Email
                </label>
                <input
                  type="email"
                  required
                  value={contactForm.email}
                  onChange={(e) => setContactForm(prev => ({ ...prev, email: e.target.value }))}
                  className="w-full rounded-lg border border-gray-300 dark:border-gray-600 px-4 py-2 focus:border-indigo-500 focus:ring-indigo-500 dark:bg-gray-700 dark:text-white"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Company
                </label>
                <input
                  type="text"
                  required
                  value={contactForm.company}
                  onChange={(e) => setContactForm(prev => ({ ...prev, company: e.target.value }))}
                  className="w-full rounded-lg border border-gray-300 dark:border-gray-600 px-4 py-2 focus:border-indigo-500 focus:ring-indigo-500 dark:bg-gray-700 dark:text-white"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Team Size
                </label>
                <select
                  required
                  value={contactForm.teamSize}
                  onChange={(e) => setContactForm(prev => ({ ...prev, teamSize: e.target.value }))}
                  className="w-full rounded-lg border border-gray-300 dark:border-gray-600 px-4 py-2 focus:border-indigo-500 focus:ring-indigo-500 dark:bg-gray-700 dark:text-white"
                >
                  <option value="">Select team size</option>
                  <option value="5-10">5-10 users</option>
                  <option value="11-50">11-50 users</option>
                  <option value="51-200">51-200 users</option>
                  <option value="201+">201+ users</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Message
                </label>
                <textarea
                  required
                  value={contactForm.message}
                  onChange={(e) => setContactForm(prev => ({ ...prev, message: e.target.value }))}
                  rows={4}
                  className="w-full rounded-lg border border-gray-300 dark:border-gray-600 px-4 py-2 focus:border-indigo-500 focus:ring-indigo-500 dark:bg-gray-700 dark:text-white"
                />
              </div>
              <div className="flex justify-end space-x-4 mt-6">
                <button
                  type="button"
                  onClick={() => setShowContactForm(false)}
                  className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:text-gray-500"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-6 py-2 bg-gradient-to-r from-indigo-500 to-purple-600 text-white rounded-lg text-sm font-semibold hover:from-indigo-600 hover:to-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                >
                  Send Message
                </button>
              </div>
            </form>
          </motion.div>
        </div>
      )}
    </div>
  );
};